import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Send } from 'lucide-react';
import { APP_CONFIG } from '../../config/app.config';

const ContactPage: React.FC = () => {
  const [sent, setSent] = useState(false);

  return (
    <div>
      <div className="page-header" style={{ paddingTop: '7rem' }}>
        <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '1rem' }}>Contact Us</h1>
        <p style={{ color: '#94a3b8', maxWidth: '480px', margin: '0 auto', fontSize: '1rem' }}>
          Have a question? Ready to schedule? We'd love to hear from you. Typical response time: under 2 hours.
        </p>
      </div>

      <div className="container-xl section-padding">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '4rem', alignItems: 'start' }}>
          {/* Info */}
          <div>
            <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Get in Touch</h2>
            {[
              { icon: <Phone size={20} color="#f97316" />, label: 'Phone', value: APP_CONFIG.phone, href: `tel:${APP_CONFIG.phoneRaw}` },
              { icon: <Mail size={20} color="#f97316" />, label: 'Email', value: APP_CONFIG.email, href: `mailto:${APP_CONFIG.email}` },
              { icon: <MapPin size={20} color="#f97316" />, label: 'Office', value: `${APP_CONFIG.address}, ${APP_CONFIG.city}, ${APP_CONFIG.state}`, href: undefined },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'flex-start' }}>
                <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.625rem', background: 'rgb(249 115 22 / 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div>
                  <p style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>{item.label}</p>
                  {item.href ? (
                    <a href={item.href} style={{ fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none', fontSize: '1rem' }}>{item.value}</a>
                  ) : (
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{item.value}</p>
                  )}
                </div>
              </div>
            ))}

            <div className="card" style={{ background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem' }}>
                <Clock size={18} color="#f97316" />
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Business Hours</span>
              </div>
              {[
                { day: 'Monday – Friday', hours: APP_CONFIG.businessHours.weekdays },
                { day: 'Saturday', hours: APP_CONFIG.businessHours.saturday },
                { day: 'Sunday', hours: APP_CONFIG.businessHours.sunday },
              ].map(row => (
                <div key={row.day} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)', fontSize: '0.875rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{row.day}</span>
                  <span style={{ fontWeight: 600, color: row.day === 'Sunday' ? '#f97316' : 'var(--text-primary)' }}>{row.hours}</span>
                </div>
              ))}
              <p style={{ marginTop: '0.875rem', fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>🚨 24/7 Emergency Line Always Available</p>
            </div>
          </div>

          {/* Form */}
          <div className="card">
            {sent ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <CheckCircle size={48} color="#10b981" style={{ marginBottom: '1rem' }} />
                <h3 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Message Sent!</h3>
                <p style={{ color: 'var(--text-secondary)' }}>We'll get back to you within 2 hours during business hours.</p>
              </div>
            ) : (
              <>
                <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Send Us a Message</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label className="label">First Name *</label>
                    <input className="input-field" placeholder="John" />
                  </div>
                  <div>
                    <label className="label">Last Name *</label>
                    <input className="input-field" placeholder="Smith" />
                  </div>
                  <div>
                    <label className="label">Phone *</label>
                    <input className="input-field" placeholder="(615) 555-0000" />
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input className="input-field" type="email" placeholder="john@email.com" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="label">Subject</label>
                    <select className="input-field">
                      <option>General Inquiry</option>
                      <option>Request an Estimate</option>
                      <option>Schedule an Inspection</option>
                      <option>Insurance Claim Help</option>
                      <option>Emergency Service</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="label">Message *</label>
                    <textarea className="input-field" rows={5} placeholder="Tell us about your roofing needs..." style={{ resize: 'vertical' }} />
                  </div>
                </div>
                <button onClick={() => setSent(true)} className="btn-primary" style={{ marginTop: '1.25rem', width: '100%' }}>
                  <Send size={16} /> Send Message
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1fr 1.5fr"]{grid-template-columns:1fr!important}div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

export default ContactPage;
